// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.1
// updated  : 2026-06-23
// protected: NO  -> Fase 4C.3: wrapper scroll horizontal untuk tabel lebar (laporan/admin).
//            Memberi AFFORDANCE scroll (shadow tepi kiri/kanan mengikuti posisi scroll +
//            hint "geser") agar tidak ada data kepotong tanpa petunjuk. Frontend-only;
//            tidak menyentuh data/logic. Mendeteksi scroller internal (mis. milik shadcn
//            <Table> yang punya overflow-auto sendiri) lewat listener fase-capture.
// =============================================================================
import React, { useRef, useState, useEffect, useCallback } from "react";
import { MoveHorizontal } from "lucide-react";

/**
 * TableScroll — bungkus konten (biasanya <Table>) dan tampilkan indikator scroll
 * horizontal yang jelas: bayangan gradien di tepi kiri/kanan saat masih ada kolom
 * tersembunyi + teks hint "Geser untuk lihat kolom lain" (mobile only).
 *
 * Props:
 *  - className : class tambahan untuk area pembungkus (mis. "max-h-96 overflow-y-auto").
 *  - hint      : tampilkan teks hint di mobile (default true).
 *  - testid    : data-testid kontainer.
 */
export const TableScroll = ({ children, className = "", hint = true, testid }) => {
  const wrapRef = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const root = wrapRef.current;
    if (!root) return;
    // Cari elemen yang benar-benar bisa discroll horizontal (root atau turunannya,
    // mis. div overflow-auto internal komponen <Table> shadcn).
    let s = null;
    if (root.scrollWidth > root.clientWidth + 1) {
      s = root;
    } else {
      const nodes = root.querySelectorAll("div,table");
      for (const el of nodes) {
        if (el.scrollWidth > el.clientWidth + 1) { s = el; break; }
      }
    }
    if (!s) { setEdges({ left: false, right: false }); return; }
    setEdges({
      left: s.scrollLeft > 4,
      right: s.scrollLeft + s.clientWidth < s.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    measure();
    const root = wrapRef.current;
    if (!root) return undefined;
    // capture:true agar event scroll dari elemen turunan (inner scroller) ikut tertangkap.
    root.addEventListener("scroll", measure, { capture: true, passive: true });
    window.addEventListener("resize", measure);
    const t1 = setTimeout(measure, 250);
    const t2 = setTimeout(measure, 800);
    return () => {
      root.removeEventListener("scroll", measure, { capture: true });
      window.removeEventListener("resize", measure);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [measure, children]);

  return (
    <div className="relative" data-testid={testid}>
      <div ref={wrapRef} className={className}>
        {children}
      </div>
      {edges.left && (
        <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-foreground/10 to-transparent" aria-hidden="true" />
      )}
      {edges.right && (
        <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-foreground/10 to-transparent" aria-hidden="true" />
      )}
      {hint && edges.right && (
        <div className="md:hidden mt-1.5 text-[11px] text-muted-foreground flex items-center gap-1"
             data-testid={testid ? `${testid}-hint` : undefined}>
          <MoveHorizontal className="w-3 h-3" /> Geser untuk lihat kolom lain
        </div>
      )}
    </div>
  );
};
